import { useSearchParams, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Check } from 'lucide-react';

import { AuthSplitLayout } from '@/components/layout/AuthSplitLayout';
import { Button } from '@agion/ui';

/**
 * Post-email-verification page.
 *
 * FLOW INVARIANT: This page must NOT redirect to admin-ui directly.
 * After email verification, the user has no SSO session yet.
 * They must sign in first — the login flow creates the SSO session,
 * and admin-ui's SessionValidator will detect onboardingState:"no_organization"
 * and route them to the organization creation form automatically.
 */
export function CreateOrganizationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const verifiedDomain = searchParams.get('verified') || '';

  const handleSignIn = () => {
    navigate('/login');
  };

  return (
    <AuthSplitLayout>
      <div className="lp-state lp-pop" role="status">
        <div className="lp-badge lp-badge-success">
          <Check size={24} aria-hidden="true" />
        </div>
        <h2 className="lp-state-title">
          {t('emailVerification.successTitle', 'Email Verified!')}
        </h2>
        <p className="lp-state-msg">
          {t('emailVerification.successMessage', 'Your email has been verified successfully.')}
        </p>

        {verifiedDomain && (
          <p className="lp-note">
            {t('emailVerification.verifiedDomain', 'Verified domain')}: <strong className="lp-emph">{verifiedDomain}</strong>
          </p>
        )}

        <hr className="lp-state-divider" />

        <p className="lp-state-prompt">
          {t('emailVerification.signInToCreateOrg', 'Sign in to set up your organization.')}
        </p>

        <Button onClick={handleSignIn} className="lp-btn-primary w-full">
          {t('emailVerification.signIn', 'Sign In')}
          <ArrowRight size={16} aria-hidden="true" />
        </Button>
      </div>
    </AuthSplitLayout>
  );
}

export default CreateOrganizationPage;
